import { ButtonAction } from "src/app/components/default/buttons/buttons.component";
import { FormField } from "src/app/components/default/form/form-field";
import { ProgressResponse } from "src/app/components/default/form/form.interface";
import { TableField } from "src/app/components/default/table/table-field";
import { TableGroup } from "src/app/components/default/table/table-group";
import { TableHeader, TableHeaderMap, TableSortDirection } from "src/app/components/default/table/table-header";
import { TableRow } from "src/app/components/default/table/table-row";
import { TABLE_TEMPLATE } from "src/app/components/default/table/table-template";
import { TableAction } from "src/app/components/default/table/table.component";
import {
  TableContentPart,
  TableOptions,
  TableResponseAttachmentLink,
  TableResponseButton,
  TableResponseData,
  TableResponseField,
  TableResponseGroup,
  TableResponseHeader,
  TableResponseRow,
} from "src/app/components/default/table/table.interface";
import { DialogService } from "src/app/components/global/dialog/dialog.service";
import { AttachmentLink } from "src/app/components/global/prefixes/impl/prefix-bes/prefix-bes.component";
import { ProgressValue } from "src/app/components/global/prefixes/templates/template-progress/template-progress.component";
import { FileManager } from "src/classes/FilesManager";
import { ROUTES_CONFIG } from "src/config/routes.config";
import { ActionResponse } from "src/interfaces/post-request/post-request";
import { ApplicationService } from "src/services/application.service";
import { HttpService } from "src/services/http.service";
import { SessionService } from "src/services/session.service";
import { ContentPart } from "../content-part";
import { ContentPartType } from "../content.component";
import { ContentForm } from "./content-form";

export class ContentTable extends ContentPart {
  public switchactive: boolean;
  public filteractive: boolean;
  public activeFilters: TableContentPart["groupfilters"];

  public options: TableOptions;

  public buttons: TableResponseButton[];
  public headers: TableHeader[];
  public root: TableGroup;

  public statusactionid: string;
  public searchactionid: string;
  public sortactionid: string;
  public searchkeys: string[];

  public tabindex: string | null;

  public constructor(
    id: string,
    http: HttpService,
    application: ApplicationService,
    type: ContentPartType = "Table",
    session: SessionService,
    dialog: DialogService,
    contentPostUrl: string,
  ) {
    super(id, type, dialog, application, session, http, contentPostUrl);
    this.session = session;

    this.switchactive = false;
    this.filteractive = false;
    this.activeFilters = [];

    this.options = {
      head: true,
      foot: true,
      delete: false,
      archive: false,
      add: false,
      download: false,
      search: true,
      filter: true,
      type: "DEFAULT",
      selectable: true,
      map: false,
      tile: false,
      pageSize: 0,
      layout: null,
    };

    this.buttons = [];
    this.headers = [];
    this.root = new TableGroup("Algemeen");

    this.statusactionid = "";
    this.searchactionid = "";
    this.sortactionid = "";
    this.searchkeys = [];

    this.tabindex = null;
  }

  public async onEvent(event: TableAction): Promise<void> {
    const type = event.type;
    const table = event.table;
    const params = event.params;
    const selectedRows = event.table.selectedRows;
    const dialogRef = event.dialogRef;

    event.loadingCallback ? event.loadingCallback(true) : this.application.loading.next(true);

    switch (type) {
      case ButtonAction.SNAPSHOT:
        await this.onSnapshot(<string>params.shift(), <string | null>params.shift());
        break;

      case ButtonAction.REDIRECT:
        await this.onRedirect(<string>params.shift());
        break;

      case ButtonAction.ACTION:
        await this.onAction(<string>params.shift(), selectedRows);
        break;

      case ButtonAction.DOWNLOAD:
        <null>params.shift(); //actionid
        await this.onDownload(selectedRows, <string | null>params.shift());
        break;

      case ButtonAction.DELETE:
        table.deleteRows();
        break;

      case ButtonAction.SAVE:
        await this.onSave(
          <FormField[]>params.shift(),
          <string | null>params.shift(),
          dialogRef,
          <boolean>params.shift(),
          <ContentForm | undefined>params.shift(),
          true,
        );
        break;

      default:
        console.warn("[FORM] Unknown table action => ", {
          type,
          table,
          params,
        });
        break;
    }
    event.loadingCallback ? event.loadingCallback(false) : this.application.loading.next(false);
  }

  /**
   * Parse TableReponseData into useable filters map
   * @param data
   * @returns
   */
  public parseFilters(group: TableResponseGroup): boolean {
    return (
      this.activeFilters.find((filter) => filter.level === group.level)?.filters.find((filter) => filter.value === group.value)?.active === "false" ||
      false
    );
  }

  public getFilterName(group: TableResponseGroup): string {
    return (
      this.activeFilters.find((_filter) => _filter.level === group.level)?.filters.find((_filter) => _filter.value === group.value)?.leveldesc ?? ""
    );
  }

  /**
   * Parse TableReponseData into useable fields map
   * @param data
   * @returns
   */
  public parse(data: TableResponseData): TableGroup {
    const group = new TableGroup("Algemeen");

    if (data) {
      group.rows = this.parseRows(data.rows);
      group.groups = this.parseGroups(data.groups);
    }

    return group;
  }

  /**
   * Parse TableResponseHeader[] into useable headers map
   * @param data
   * @returns
   */
  public parseHeaders(data: TableContentPart): TableHeader[] {
    const headers: TableHeader[] = [];
    if (data.headerfields)
      for (const head of data.headerfields) {
        const width = head?.width;
        const name = head.orgname || head.pfx + head.name;
        const label = this.sanitizeName(head);

        let map: TableHeaderMap = TableHeaderMap.NONE;
        if (data.layout && data.layout?.map_columns.pin === name) {
          map = TableHeaderMap.PIN;
        } else if (data.layout?.map_columns.popup.includes(name)) {
          map = TableHeaderMap.POPUP;
        }

        const tile = data.layout ? data.layout?.tile_columns.includes(name) : false;
        const list = data.layout ? data.layout?.list_columns.includes(name) : true;

        const header = new TableHeader(name, label, {
          icon: head.headericon,
          width: width ? `${width}px` : undefined,
          list,
          map,
          tile,
        });
        if (head.sortorder) {
          header.sort = {
            column: head.sortcolumn || null,
            direction:
              head.sortorder && ["asc", "desc", "initial"].includes(head.sortcolumn) ? <TableSortDirection>head.sortcolumn.toUpperCase() : "INITIAL",
          };
        }
        header.visible = head.hidecolumn == "0";
        header.template = head.pfx;
        headers.push(header);
      }

    return headers;
  }

  /**
   * Set correct values
   * @param field
   * @returns
   */
  public sanitizeValue(field: TableResponseField): unknown {
    switch (field.pfx) {
      case "img":
        return (<string>field.value).split("|")[0];

      case "jan":
        return this.sanitizeBoolean(field.value);

      case "prg":
      case "pge":
        return (<ProgressValue>(<unknown>this.parseProgress(<ProgressResponse>field.value))).current;

      case "geo":
        if (typeof field.value === "object" && field.value !== null && "lat" in field.value && "lon" in field.value) {
          return field.value;
        } else {
          return null;
        }

      default:
        return field.value;
    }
  }

  /**
   * Create the extras object depending on prefix
   * @param field
   * @returns
   */
  public getExtras(field: TableResponseField): Record<string, unknown> {
    switch (field.pfx) {
      case "img":
        return this.parseImage(<string>field.value);

      case "lnk":
      case "ln3":
      case "lna":
      case "idp":
        return this.parseLink(field.l);

      case "lni":
        return { ...this.parseLink(field.l), size: 48 };

      case "prg":
      case "pge":
        return { options: this.parseProgress(<ProgressResponse>field.value) };

      case "bes":
        return { attachmentLinks: this.parseAttachmentLinks(field.a) };

      default:
        return {};
    }
  }

  /*
   * parses data into rows
   * @param rows
   * @param data
   */
  private parseRows(data: TableResponseRow[]): TableRow[] {
    const rows: TableRow[] = [];
    for (const _row of data) {
      const row = new TableRow();
      row.deletelink = _row.deletelink;
      row.downloadlink = _row.downloadlink;

      for (const [fieldIndex, field] of _row.fields.entries()) {
        const head = this.headers.at(fieldIndex);
        if (head) {
          let tooltip: string | null = null;
          tooltip = field.tt.map((tooltip) => tooltip.value).join("<br />");
          const extras = this.getExtras(field);
          const value = this.sanitizeValue(field);
          const ffipe = field.ffipe;
          row.fields.push(
            new TableField(head, head.name, TABLE_TEMPLATE[field.pfx] || field.pfx || TABLE_TEMPLATE.DEFAULT, value, tooltip, extras, ffipe),
          );
        } else {
          throw new Error(`Could not find header => ${fieldIndex}`);
        }
      }

      rows.push(row);
    }

    return rows;
  }

  /**
   * parses data into groups
   * @param groups
   * @param data
   */
  private parseGroups(data: TableResponseGroup[]): TableGroup[] {
    const groups: TableGroup[] = [];
    for (const group of data) {
      const _group = new TableGroup(group.value);
      _group.level = group.level;
      _group.hidden = this.parseFilters(group);
      _group.rows = this.parseRows(group.rows);
      _group.groups = this.parseGroups(group.groups);
      _group.fieldname = this.getFilterName(group);
      groups.push(_group);
    }

    return groups;
  }

  private parseAttachmentLinks(attachmentLinks: TableResponseAttachmentLink[]): AttachmentLink[] {
    return attachmentLinks.map((link) => ({
      description: link.description,
      extension: link.omsExtensie,
      filename: link.pa2Bestandsnaam,
      link: link.link,
      originalFilename: link.padOriginalFilename,
    }));
  }

  private sanitizeName(head: TableResponseHeader): string {
    switch (head.pfx) {
      default:
        return head.name.replace(/_/g, "\n");
    }
  }

  /**
   * Do api action
   */
  private async action<T>(actionid: string, actiondata = "", responseType = "json"): Promise<T> {
    return this.http.send<T>(
      ROUTES_CONFIG.actionurl,
      {
        FFWDActionID: actionid,
        actionData: actiondata,
      },
      {
        responseType: <"json">responseType,
      },
    );
  }

  /**
   * Do api action and observe response
   */
  private async actionObserve<T>(actionid: string, actiondata = "", responseType = "json", headersOut?: Headers): Promise<T> {
    return this.http.send(
      ROUTES_CONFIG.actionurl,
      {
        FFWDActionID: actionid,
        actionData: actiondata,
      },
      {
        responseType: <"json">responseType,
        observe: <"body">"response",
        responseHeadersOut: headersOut,
      },
    );
  }

  /**
   * Event listener on form action snapshot
   */
  private async onSnapshot(actionid: string, filename: string | null): Promise<void> {
    try {
      if (filename) {
        FileManager.download({
          blob: await this.action<Blob>(actionid, "", "blob"),
          label: filename,
        });
      } else {
        const headers = new Headers();
        const response = await this.actionObserve<Blob>(actionid, "", "blob", headers);

        if (!response) throw new Error("Unable to download. Response body was empty");

        FileManager.download({
          blob: response,
          label: this.getFilenameFromHeader(headers) ?? "null",
        });
      }
    } catch (e) {
      console.error("Unable to snapshot", e);
    }
  }

  /**
   * Event listener on form action redirect
   */
  private async onRedirect(contentid: string): Promise<boolean> {
    return this.application.router.navigate(["/app/content", contentid]);
  }

  /**
   * Event listener on form action action
   */
  private async onAction(actionid: string, rows: TableRow[] = []): Promise<void> {
    try {
      // const response = await this.action<ActionResponse>(actionid);
      // const parsed = <ActionResult>JSON.parse(response.postActionAsJSON);
      // this.application.onMessage(parsed.messages);

      // const formdata = await this.getFormData(fields);
      let parsed = { messages: [] };
      if (actionid.startsWith("/")) {
        //parse input

        const idps = rows.map((e) =>
          Array.from(e.fields.values())
            .filter((a) => a.template == ("idp" as TABLE_TEMPLATE))
            .map((a) => a.value.value)
            .at(0),
        );

        const input = {
          selected: idps,
        };
        //dynamic action

        const head = await fetch(`${ROUTES_CONFIG.dynactionUrl}${actionid}`, {
          method: "post",
          body: JSON.stringify(input),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.session?.id}`,
          },
        });
        if (head.headers.has("Content-Disposition")) {
          //download
          return void FileManager.downloadDyn(head);
        }
        const response = await head.json();
        parsed = response;
      } else {
        const response = await this.action<ActionResponse>(actionid);
        parsed = JSON.parse(response.postActionAsJSON);
      }

      // const parsed = <ActionResult>JSON.parse(response.postActionAsJSON);
      this.application.onMessage(parsed.messages);
    } catch (e) {
      console.error("Unable to action", e);
    }
  }

  /**
   * Event listener on form action download
   */
  private async onDownload(rows: TableRow[], filename: string | null): Promise<void> {
    try {
      if (rows.length) {
        const obj: Record<string, string> = {};
        for (const [index, row] of rows.entries()) {
          obj[`downloadlink${index}`] = row.downloadlink;
        }

        if (filename) {
          FileManager.download({
            blob: await this.action<Blob>(Object.values(obj)[0], JSON.stringify(obj), "blob"),
            label: filename,
          });
        } else {
          const headers = new Headers();
          const response = await this.actionObserve<Blob>(Object.values(obj)[0], JSON.stringify(obj), "blob", headers);

          if (!response) throw new Error("Unable to download. Response body was empty");

          FileManager.download({
            blob: response,
            label: this.getFilenameFromHeader(headers) ?? "null",
          });
        }
      } else {
        throw new Error("No selected rows.");
      }
    } catch (e) {
      console.error("Unable to download", e);
    }
  }

  /**
   * Event listener on table action archive
   */
  private async onArchive(): Promise<void> {
    throw new Error("Method not implemented.");
  }

  /**
   * Event listener on table action delete
   */
}
